import { ReactNode } from 'react';

import { useImageLoad } from '@backrs_ui/common-ui/src/hooks/useImageLoad';

import { Box, Skeleton, SxProps, Theme, Typography, Chip } from '@mui/material';

const MAX_IMAGE_WIDTH = 300;

export type ImageCardProps = Readonly<{
  imageUrl: string;
  title: string;
  beta?: boolean;
  description?: string | ReactNode;
  onClick?: () => void;
  children?: ReactNode;
  sxOverrides?: {
    container?: SxProps<Theme>;
    title?: SxProps<Theme>;
    description?: SxProps<Theme>;
    image?: SxProps<Theme>;
    imageWrapper?: SxProps<Theme>;
    titleDescChildContainer?: SxProps<Theme>;
  };
}>;

export const ImageCard = ({
  title,
  description,
  imageUrl,
  beta,
  onClick,
  children,
  sxOverrides,
}: ImageCardProps) => {
  const image = useImageLoad(imageUrl);

  return (
    <Box sx={[containerSx, sxOverrides?.container] as SxProps<Theme>} onClick={onClick}>
      {!image.imageError && (
        <Box sx={[imageWrapperSx, sxOverrides?.imageWrapper] as SxProps<Theme>}>
          {!image.imageLoaded && (
            <Skeleton variant="rectangular" animation="wave" sx={imageSkeletonSx} />
          )}
          <Box
            component="img"
            src={imageUrl}
            alt={title}
            sx={[imageWithLoadSx(image.imageLoaded), sxOverrides?.image] as SxProps<Theme>}
            {...image.imageProps}
          />
        </Box>
      )}
      <Box sx={[titleDescContainerSx, sxOverrides?.titleDescChildContainer] as SxProps<Theme>}>
        <Box>
          <Box sx={titleContainerSx}>
            <Typography
              variant="text-lg-semibold"
              sx={sxOverrides?.title}
              color={({ backrs }) => backrs.colors['Colors/Text/text-primary']}
            >
              {title}
            </Typography>
            {beta && <Chip label="Beta" variant="pill" size="lg" sx={betaChipSx} />}
          </Box>
          {description && (
            <Box>
              <Typography
                variant="text-sm-regular"
                color={({ backrs }) => backrs.colors['Colors/Text/text-tertiary']}
                sx={sxOverrides?.description}
              >
                {description}
              </Typography>
            </Box>
          )}
        </Box>

        {children}
      </Box>
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  borderRadius: backrs.radius['radius-xl'],
  overflow: 'hidden',
  border: '1px solid black',
  width: 'fit-content',
  maxWidth: { xs: '100%', sm: MAX_IMAGE_WIDTH },
  cursor: 'pointer',
  transition: 'all 0.3s ease-in-out',
  userSelect: 'none',
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  '&:hover': {
    boxShadow: backrs.shadows['shadow-xl'],
    transform: 'translate(-2px, -2px)',
  },
  '&:active': {
    outline: 'none',
    transform: 'translate(0px, 0px)',
    boxShadow: 'none',
    transition: 'all 0s',
  },
});

const imageWrapperSx: SxProps<Theme> = {
  position: 'relative',
  overflow: 'hidden',
  aspectRatio: '3 / 2',
};

const imageWithLoadSx = (loaded: boolean): SxProps<Theme> => ({
  height: '100%',
  width: '100%',
  objectFit: 'cover',
  display: 'block',
  borderBottom: '1px solid black',
  opacity: loaded ? 1 : 0,
  transition: 'opacity 0.3s ease-in-out',
});

const imageSkeletonSx: SxProps<Theme> = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
};

const titleDescContainerSx: SxProps<Theme> = ({ backrs }) => ({
  gap: backrs.spacing['spacing-xxs'],
  px: backrs.spacing['spacing-xl'],
  py: backrs.spacing['spacing-2xl'],
});

const betaChipSx: SxProps<Theme> = ({ backrs }) => ({
  border: `1px solid ${backrs.colors['Brand/200']}`,
  backgroundColor: backrs.colors['Brand/50'],
  color: backrs.colors['Brand/700'],
  objectFit: 'contain',
  gap: backrs.spacing['spacing-sm'],
  height: '22px',
});

const titleContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-sm'],
});
